import React, { useState, useEffect } from "react";
import { clearSession } from "../shared/_helpers/session";
import { Link, useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { checkBlockedStatus, getProfile, updateProfile, removeProfileImage, getCountryCode } from "../shared/_services/common.services";
import { changePassword } from "../shared/_services/auth.services";
import { readCurrentUser } from "../shared/_helpers/currentUser";
import { hasUnlimitedMeetings, meetingsAvailableCount } from "../shared/_helpers/meetingEntitlement";
import { useSnackbar } from "notistack";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Subscription from "./Subscription";
import AdvisorReviewsCard from "./AdvisorReviewsCard";
import Loader from "./Loader";

// D2: email and mobile are the login identifiers, so the server only lets a
// super-admin change them (403 CONTACT_CHANGE_NOT_ALLOWED otherwise).
export const CONTACT_CHANGE_BLOCKED_MESSAGE = "Contact support to change your email or phone.";

function isContactChangeRefused(result) {
  return (
    result?.response?.status === 403 &&
    result?.response?.data?.code === "CONTACT_CHANGE_NOT_ALLOWED"
  );
}

//updated code

function getProfileInitials(userData) {
  const firstInitial = userData?.firstName ? userData.firstName.trim().charAt(0) : "";
  const lastInitial = userData?.lastName ? userData.lastName.trim().charAt(0) : "";
  return `${firstInitial}${lastInitial}`;
}

function shouldShowProfileImage(previewImage, userData) {
  return Boolean(previewImage || (userData?.profile_image && userData.profile_image.trim() !== ""));
}

function getProfileImageSrc(previewImage, userData) {
  return previewImage || userData?.profile_image;
}

function getInvalidControlClass(hasError) {
  return hasError ? "form-control is-invalid" : "form-control";
}

function redirectToLogin(navigate) {
  clearSession();
  navigate("/login");
}

async function runBlockedCheck(navigate) {
  const userId = JSON.parse(localStorage.getItem("currentUser"))?.user?._id;
  try {
    const response = await checkBlockedStatus(userId);
    const isUnauthorized = response.data ? response.data.status : response.response.status == 401;

    if (isUnauthorized) {
      redirectToLogin(navigate);
      return;
    }

    const { isBlocked, isDeleted } = response.data;
    if (isBlocked || isDeleted) {
      redirectToLogin(navigate);
    }
  } catch (error) {
    console.error("Error checking blocked status:", error);
  }
}

async function fetchAndSetUserDetails(setShowProfileLoader, setUserData, navigate, enqueueSnackbar) {
  setShowProfileLoader(true);
  const currentUser = JSON.parse(localStorage.getItem("currentUser"));
  const userId = currentUser?.user?._id;
  const data = await getProfile(userId);
  setShowProfileLoader(false);

  if (data.status === 200) {
    setUserData(data?.data?.data?.user);
    return;
  }

  if (data?.response?.status === 401) {
    clearSession();
    enqueueSnackbar("Your session has been expired!", {
      variant: "error",
      anchorOrigin: { horizontal: "right", vertical: "top" },
    });
    navigate("/login");
  }
}

function buildProfileFormData(values) {
  const { name, email, mobile, profile_image, code } = values;
  const formData = new FormData();
  formData.append("firstName", name.split(" ")[0]);
  formData.append("lastName", name.split(" ").slice(1).join(" "));
  // D2: read-only fields, sent back exactly as stored (no lowercasing) and only
  // when present, together with the stored country code. The server refuses any
  // value that differs from what it holds, and an empty string would count as
  // "different" for a client who has no email or no phone.
  if (email) formData.append("email", email);
  if (mobile) formData.append("mobile", mobile);
  formData.append("code", code);

  if (profile_image instanceof File) {
    formData.append("profile_image", profile_image);
  }

  return formData;
}

function syncUpdatedUserToStorage(profileData) {
  const currentUser = JSON.parse(localStorage.getItem("currentUser"));
  const updatedLocalStorage = { ...currentUser };
  updatedLocalStorage.user = { ...currentUser.user, ...profileData };
  localStorage.setItem("currentUser", JSON.stringify(updatedLocalStorage));
  return updatedLocalStorage?.user?._id;
}

async function submitProfileUpdate(values, setSubmitting, deps) {
  const {
    setUserData,
    setPreviewImage,
    setShowEditProfile,
    fetchUserDetails,
    enqueueSnackbar,
  } = deps;

  const userId = JSON.parse(localStorage.getItem("currentUser"))?.user?._id;
  const formData = buildProfileFormData(values);
  const data = await updateProfile(userId, formData);
  const responseUserData = data?.data?.data;

  syncUpdatedUserToStorage(responseUserData);
  setUserData((prevData) => ({
    ...prevData,
    data: { ...prevData.data, ...responseUserData },
  }));

  if (data.status == 200) {
    enqueueSnackbar("Profile updated successfully.", { variant: "success" });
  } else if (isContactChangeRefused(data)) {
    enqueueSnackbar(CONTACT_CHANGE_BLOCKED_MESSAGE, { variant: "error" });
  } else {
    enqueueSnackbar(data?.response?.data?.message || "Profile update failed", { variant: "error" });
  }

  setSubmitting(false);
  setPreviewImage(null);
  setShowEditProfile(true);
  fetchUserDetails();
}

async function removeProfileImageWithFeedback(setFieldValue, event, deps) {
  const { previewImage, setPreviewImage, enqueueSnackbar, setUserData } = deps;
  if (event) event.preventDefault();

  if (previewImage) {
    setPreviewImage(null);
    setFieldValue("profile_image", null);
    enqueueSnackbar("Preview Profile image removed successfully.", { variant: "success" });
    return;
  }

  const userId = JSON.parse(localStorage.getItem("currentUser"))?.user?._id;
  const response = await removeProfileImage(userId);

  if (response.status === 200) {
    setPreviewImage(null);
    setFieldValue("profile_image", "");
    setUserData((prevData) => ({
      ...prevData,
      profile_image: "",
    }));
    enqueueSnackbar("Profile image removed successfully.", { variant: "success" });
    return;
  }

  enqueueSnackbar("Failed to remove profile image.", { variant: "error" });
}

function ChangePasswordModal({
  show,
  onClose,
  enqueueSnackbar,
  showOldPassword,
  setShowOldPassword,
  showNewPassword,
  setShowNewPassword,
  showConfirmPassword,
  setShowConfirmPassword,
}) {
  return (
    <Modal
      size="md"
      className="editProfilePopup"
      show={show}
      onHide={onClose}
      aria-labelledby="changePasswordPopupLabel"
      backdrop="static"
      keyboard={false}
      centered
    >
      <div className="modal-body px-4 py-4">
        <div className="d-flex justify-content-between">
          <h5>Change Password</h5>
          <Link className="close-popup-btn" onClick={onClose}>
            <i className="iconoir-xmark"></i>
          </Link>
        </div>
        <p style={{ fontSize: "14px", fontWeight: "400", color: "#9F9F9F", width: "70%", marginBottom: "24px" }}>
          Please provide your current password and set a
          new one to continue.
        </p>
        <Formik
          initialValues={{
            oldPassword: "",
            password: "",
            confirmPassword: "",
          }}
          validationSchema={Yup.object().shape({
            oldPassword: Yup.string().required("Current password is required"),
            password: Yup.string()
              .required("New password is required")
              .matches(
                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[`~!@#$%^&*()_\-+=[\]{};:'",./<>?\\|]).{8,}$/,
                "Password should contain atleast 1 number, 1 lowercase, 1 uppercase, 1 special character and must of 8 digits."
              ),
            confirmPassword: Yup.string()
              .required("Confirm password is required")
              .oneOf([Yup.ref("password"), null], "Passwords must match")
              .matches(
                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[`~!@#$%^&*()_\-+=[\]{};:'",./<>?\\|]).{8,}$/,
                "Password should contain atleast 1 number, 1 lowercase, 1 uppercase, 1 special character and must of 8 digits."
              ),
          })}
          onSubmit={async (values) => {
            const { password, oldPassword } = values;
            // Captured before the request, not after: localStorage is shared
            // across tabs, so a sign-out/sign-in to a DIFFERENT account in
            // another tab while this request is in flight must not let this
            // tab's response merge its fresh tokens into that other account's
            // stored identity — that would pair one account's token with a
            // different account's user object, a real identity/token
            // mismatch. Comparing the account id (not the full token) is
            // deliberate: this endpoint revokes every other session on THIS
            // account as part of a successful change, so the response's pair
            // is guaranteed valid for this account by construction. A
            // same-account re-sign-in from another tab racing this request is
            // a real but narrower case — comparing the token instead of the
            // id was tried and reverted (see PR #81 review) because it can't
            // tell "an intervening same-account session that is now stale"
            // from "one that's genuinely newer," and discarding the
            // guaranteed-valid response in favor of a possibly-already-revoked
            // one causes a worse failure: a false logout right after a
            // successful password change. No local signal can resolve that
            // ambiguity; it would need the server to hand back a session
            // identifier to order against.
            const accountIdBeforeChange = readCurrentUser()?.user?._id;
            const data = await changePassword(oldPassword, password);

            if (data.status == 200) {
              // SEC-084/SEC-115: a change revokes every session on the account,
              // including this one, so the success response carries a fresh
              // {token, refreshToken} pair. Without persisting it, the very next
              // authenticated request from this tab 401s on the now-revoked token.
              const currentUser = readCurrentUser() || {};
              if (
                currentUser.user &&
                currentUser.user._id === accountIdBeforeChange &&
                data?.data?.token
              ) {
                currentUser.token = data.data.token;
                currentUser.refreshToken = data.data.refreshToken;
                localStorage.setItem("currentUser", JSON.stringify(currentUser));
              }

              enqueueSnackbar(data?.data?.message, {
                variant: "success",
              });
              onClose();
              return;
            }

            enqueueSnackbar(
              data.response ? data?.response?.data?.message : data?.data?.message,
              {
                variant: "error",
              }
            );
          }}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form>
              <div className="form-group password-field">
                <label htmlFor="old_password">Current Password</label>
                <Field
                  type={showOldPassword ? "text" : "password"}
                  name="oldPassword"
                  id="oldPassword"
                  placeholder="Enter Current Password"
                  className={`form-control ${errors.oldPassword && touched.oldPassword
                    ? "is-invalid"
                    : ""
                    }`}
                />
                <ErrorMessage
                  name="oldPassword"
                  component="div"
                  className="invalid-feedback"
                />
                <span
                  className="toggle-password"
                  onClick={() => { setShowOldPassword(!showOldPassword) }}
                >
                  {showOldPassword ? (
                    <i className="iconoir-eye-closed"></i>
                  ) : (
                    <i className="iconoir-eye"></i>
                  )}
                </span>
              </div>

              <div className="form-group password-field">
                <label htmlFor="new_password">New Password</label>
                <Field
                  type={showNewPassword ? "text" : "password"}
                  name="password"
                  id="password"
                  placeholder="Enter New Password"
                  className={`form-control ${errors.password && touched.password
                    ? "is-invalid"
                    : ""
                    }`}
                />
                <ErrorMessage
                  name="password"
                  component="div"
                  className="invalid-feedback"
                />
                <span
                  className="toggle-password"
                  onClick={() => { setShowNewPassword(!showNewPassword) }}
                >
                  {showNewPassword ? (
                    <i className="iconoir-eye-closed"></i>
                  ) : (
                    <i className="iconoir-eye"></i>
                  )}
                </span>
              </div>

              <div className="form-group password-field">
                <label htmlFor="reenter_new_password">
                  Confirm Password
                </label>
                <Field
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  id="confirmPassword"
                  placeholder="Enter Confirm Password"
                  className={`form-control ${errors.confirmPassword && touched.confirmPassword
                    ? "is-invalid"
                    : ""
                    }`}
                />
                <ErrorMessage
                  name="confirmPassword"
                  component="div"
                  className="invalid-feedback"
                />
                <span
                  className="toggle-password"
                  onClick={() => { setShowConfirmPassword(!showConfirmPassword) }}
                >
                  {showConfirmPassword ? (
                    <i className="iconoir-eye-closed"></i>
                  ) : (
                    <i className="iconoir-eye"></i>
                  )}
                </span>
              </div>
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="as-btn-primary mt-3 w-75"
                >
                  Change Password
                </button>
              </div>

            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
}

const Profile = () => { // NOSONAR
  
  const [changePasswordPopupShow, setChangePasswordPopupShow] = useState(false);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [userData, setUserData] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showProfileLoader, setShowProfileLoader] = useState(false);
  const [countrycode, setCountryCodes] = useState([]);

  const currentUser = JSON.parse(localStorage.getItem('currentUser'))?.user;
  const subscribed = !!currentUser?.selected_plan;
  // payment_status is a boolean on the backend (model/user.js); this used to
  // compare against the string "false", which a boolean never equals, so a
  // lapsed subscriber's profile never showed the payment-incomplete state.
  const isPaymentIncomplete = !currentUser?.selected_plan || !currentUser?.payment_status;


  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, "");
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
    }
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  // Email and mobile are read-only (D2) and owned by the server, so they are
  // not validated here: a stored value in an older format must not block
  // saving a name change.
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("Name is required")
      .matches(/^[A-Za-z\s]*$/, "Name cannot contain numbers") // Ensure no numbers
      .test("valid-name", "Enter a valid name", (value) => {
        return !!value && value.trim().length > 0; // Ensure at least 1 valid character
      })
      .min(3, "Name must be at least 3 characters")
      .max(30, "Name must be at most 30 characters"),
  });
  const checkBlocked = async () => {
    await runBlockedCheck(navigate);
  }


  const fetchUserDetails = async () => {
    await fetchAndSetUserDetails(setShowProfileLoader, setUserData, navigate, enqueueSnackbar);
  };
  const fetchCountryCodes = async () => {
    const code = await getCountryCode();
    setCountryCodes(code?.data);
  };

  useEffect(() => {
    fetchCountryCodes();
    checkBlocked();
    fetchUserDetails();
  }, []); // eslint-disable-line


  const handleSubmit = async (values, { setSubmitting }) => {
    await submitProfileUpdate(values, setSubmitting, {
      setUserData,
      setPreviewImage,
      setShowEditProfile,
      fetchUserDetails,
      enqueueSnackbar,
    });
  };

  const handleRemoveProfileImage = async (setFieldValue, event) => {
    await removeProfileImageWithFeedback(setFieldValue, event, {
      previewImage,
      setPreviewImage,
      enqueueSnackbar,
      setUserData,
    });
  };

  
  const changePasswordPopupClose = () => setChangePasswordPopupShow(false);

  const openChangePasswordPopup = () => {
    
    setChangePasswordPopupShow(true);
  };

  

  const refressUser = (status) => {
    if (status) fetchUserDetails();
  }

  const [showEditProfile, setShowEditProfile] = useState(true);

  return (
    <>
      <div className="page-header mb-3">
        <h2>Profile</h2>
        <p>Every detail about you, right where it belongs.</p>
      </div>

      {!showProfileLoader ? (
        <div className="px-4 pb-4">
          <div className="row">
            <div className="col-5">
              <div className="ast-card profile-card">
                <h4 className="ast-card-heading">Profile</h4>

                {showEditProfile ? (
                  <div>
                    <div className="d-flex justify-content-between mb-4">
                      <div className="d-flex">
                        <div className="profile-pic">
                          {userData?.profile_image ? (
                            <img src={userData?.profile_image} alt="Profile" />
                          ) : (
                            <h3>
                              {userData?.firstName ? userData?.firstName.trim().charAt(0) : ""}
                              {userData?.lastName ? userData?.lastName.trim().charAt(0) : ""}
                            </h3>
                          )}
                        </div>

                        <div className="ml-3">
                          <h5
                            className="name mt-3 text-break"
                            title={`${userData?.firstName} ${userData?.lastName}`}
                          >
                            {userData?.firstName || userData?.lastName ? (
                              (() => {
                                const fullName = `${userData?.firstName} ${userData?.lastName}`;
                                const isTruncated = (userData?.firstName + userData?.lastName).length > 20;
                                return fullName.slice(0, 20) + (isTruncated ? "..." : "");
                              })()
                            ) : (
                              ""
                            )}
                          </h5>
                        </div>
                      </div>
                      <div
                        onClick={() => setShowEditProfile(!showEditProfile)}
                        className="cursor-pointer edit-profile-popup-btn mt-2"
                      >
                        <i className="fa fa-pencil"></i>
                      </div>
                    </div>
                    <p className="email mb-4">
                      <b>Email{" "}</b>
                      <br /><span className="text-secondary f-400">{userData?.email ? `${userData?.email} ` : ""}</span>
                    </p>
                    <p className="phone mb-4">
                      <b>Phone Number{" "}</b>
                      <br /><span className="text-secondary f-400">{userData?.mobile ? `${userData?.mobile} ` : ""}</span>
                    </p>
                    <p className="password">
                      <b>Password</b>
                      <br /><span className="text-secondary f-400">**********</span>
                    </p>
                  </div>
                ) : (
                  <div>
                    <Formik
                      initialValues={{
                        name: `${userData?.firstName || ""} ${userData?.lastName || ""}`,
                        email: userData?.email || "",
                        mobile: userData?.mobile || "",
                        profile_image: userData?.profile_image || "",
                        code: userData?.countryCode || "",
                      }}
                      validationSchema={validationSchema}
                      onSubmit={handleSubmit}
                    >
                      {({ errors, touched, isSubmitting, values, setFieldValue }) => (
                        <Form>
                          <div className="profile-pic d-flex justify-content-start mb-3">
                            {shouldShowProfileImage(previewImage, userData) ? (
                              <>
                                <img src={getProfileImageSrc(previewImage, userData)} alt="Profile" />
                                <button
                                  className="removeProfileImage"
                                  onClick={(event) => handleRemoveProfileImage(setFieldValue, event)} // Wrap in a function
                                >
                                  -
                                </button>
                              </>
                            ) : (
                              <h3>{getProfileInitials(userData)}</h3>
                            )}
                            <div className="avatar-edit">
                              <input
                                type="file"
                                id="imageUpload"
                                accept=".png, .jpg, .jpeg"
                                onChange={(event) => {
                                  const file = event.currentTarget.files[0];
                                  setFieldValue("profile_image", file); // Update Formik with the new image
                                  setPreviewImage(URL.createObjectURL(file)); // Show image preview
                                }}
                              />
                              <label htmlFor="imageUpload"></label>
                            </div>
                          </div>

                          <div className="form-group">
                            <label htmlFor="name">Name</label>
                            <Field
                              type="text"
                              id="name"
                              name="name"
                              placeholder="Enter your name"
                              className={getInvalidControlClass(Boolean(errors.name && touched.name))}
                            />
                            <ErrorMessage name="name" component="div" className="invalid-feedback" />
                          </div>
                          <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <Field
                              type="email"
                              name="email"
                              id="email"
                              readOnly
                              aria-describedby="contact-locked-help"
                              className={getInvalidControlClass(false)}
                            />
                          </div>

                          <div className="form-group input-phone-con">
                            <label htmlFor="mobile">Phone Number</label>
                            <div className="d-flex flex-wrap">
                              <Field as="select" name="code" disabled>
                                {countrycode?.map((item) => (
                                  <option key={item.code} value={item.code}>
                                    ({item.code})
                                  </option>
                                ))}
                              </Field>
                              <Field
                                type="text"
                                id="mobile"
                                name="mobile"
                                readOnly
                                aria-describedby="contact-locked-help"
                                className={getInvalidControlClass(false)}
                                value={formatPhoneNumber(values.mobile)}
                              />
                            </div>
                            <small id="contact-locked-help" className="text-secondary">
                              To change your email or phone, contact support.
                            </small>
                          </div>

                          <p className="password">
                            <label>Password</label>
                            <br /><span className="text-secondary f-400">**********</span>
                            <br />
                            <Link
                              onClick={openChangePasswordPopup}
                              className="text-astra f-600 cursor-pointer"
                              style={{ fontSize: '12px', textDecoration: 'underline' }}
                            >
                              Change Password
                            </Link>
                          </p>
                          <div className="d-flex justify-content-end gap-10 mt-5">
                            <button type="submit" onClick={() => setShowEditProfile(!showEditProfile)} className="as-btn-secondary py-2 px-4">
                              Cancel
                            </button>
                            <button type="submit" disabled={isSubmitting} className="as-btn-primary py-2 px-4">
                              Update Profile
                            </button>
                          </div>

                        </Form>
                      )}
                    </Formik>
                  </div>
                )}
              </div>
            </div>


            <div className="col-7">
              {JSON.parse(localStorage.getItem("currentUser"))?.user?.userType === 'client' && (
                hasUnlimitedMeetings(userData) ? (
                  // UNLIMITED MEETINGS
                  <div className="remaining-meeting-card mb-4">
                    <h3>
                      <span>Unlimited Meetings</span>
                    </h3>
                  </div>
                ) : (
                  // LIMITED MEETINGS. One number, not `consumed/total`: backend
                  // ARCH-036 removed both of those and there is no consumed/total
                  // split any more — the plan's annual allowance and the meetings
                  // the customer bought are separate buckets now, and what the
                  // heading has always promised is the sum of what is left. The
                  // card rendered " 0/" against the projected payload, since both
                  // sides of the fraction were `undefined`.
                  <div className="remaining-meeting-card mb-4">
                    <h3>
                      <span>Number of Meetings Left</span>
                      <> <strong>{meetingsAvailableCount(userData)}</strong></>
                    </h3>
                  </div>
                )
              )}
              {!subscribed && userData.userType === "client" && (
                <div className="ast-card">
                  <h4 className="ast-card-heading">Unlock Your Full Access</h4>
                  <div className="subscribe-adv">
                    <p className="text-secondary f-400 text-14 mb-4">Subscribe now to connect Plaid, sync, your accounts in real-time,<br />
                      and unlock premium advisor features.</p>
                  </div>
                  <div className="row">
                    <div className="col-6">
                      <div className="subscribe-adv-card mb-4">
                        <svg width="36" height="42" viewBox="0 0 36 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M4.72708 21.9993H10.3333C10.6847 21.9993 11.0201 22.0875 11.3396 22.2638C11.659 22.4402 11.8986 22.7034 12.0583 23.0535L14.1667 27.2702L20.1083 15.3869C20.4597 14.6521 21.0347 14.2848 21.8333 14.2848C22.6319 14.2848 23.2069 14.6521 23.5583 15.3869L26.8646 21.9993H31.2729C30.7937 18.741 29.3083 16.0098 26.8167 13.8056C24.325 11.6014 21.3861 10.4993 18 10.4993C14.6139 10.4993 11.675 11.6014 9.18333 13.8056C6.69167 16.0098 5.20625 18.741 4.72708 21.9993ZM18 37.3327C21.3861 37.3327 24.325 36.2306 26.8167 34.0264C29.3083 31.8223 30.7937 29.091 31.2729 25.8327H25.6667C25.3153 25.8327 24.9799 25.7452 24.6604 25.5701C24.341 25.395 24.1014 25.1312 23.9417 24.7785L21.8333 20.5618L15.8917 32.4452C15.5403 33.1799 14.9653 33.5473 14.1667 33.5473C13.3681 33.5473 12.7931 33.1799 12.4417 32.4452L9.13542 25.8327H4.72708C5.20625 29.091 6.69167 31.8223 9.18333 34.0264C11.675 36.2306 14.6139 37.3327 18 37.3327ZM18 41.166C15.6361 41.166 13.4083 40.7111 11.3166 39.8013C9.22486 38.8916 7.39572 37.6534 5.82917 36.0869C4.26261 34.5203 3.02508 32.6912 2.11658 30.5994C1.20808 28.5077 0.752556 26.2799 0.75 23.916H4.58333C4.58333 27.6216 5.89306 30.7841 8.5125 33.4035C11.1319 36.023 14.2944 37.3327 18 37.3327C21.7056 37.3327 24.8681 36.023 27.4875 33.4035C30.1069 30.7841 31.4167 27.6216 31.4167 23.916H35.25C35.25 26.2799 34.7951 28.5084 33.8853 30.6014C32.9756 32.6944 31.7374 34.5229 30.1708 36.0869C28.6043 37.6509 26.7751 38.889 24.6834 39.8013C22.5917 40.7137 20.3639 41.1686 18 41.166ZM0.75 23.916C0.75 21.5521 1.20553 19.3237 2.11658 17.2307C3.02764 15.1377 4.26517 13.3092 5.82917 11.7452C7.39317 10.1812 9.2223 8.94365 11.3166 8.0326C13.4109 7.12154 15.6387 6.66602 18 6.66602C19.9806 6.66602 21.8813 6.98546 23.7021 7.62435C25.5229 8.26324 27.2319 9.18963 28.8292 10.4035L31.5125 7.72018L34.1958 10.4035L31.5125 13.0868C32.7264 14.6841 33.6528 16.3931 34.2917 18.2139C34.9306 20.0348 35.25 21.9355 35.25 23.916H31.4167C31.4167 20.2105 30.1069 17.048 27.4875 14.4285C24.8681 11.8091 21.7056 10.4993 18 10.4993C14.2944 10.4993 11.1319 11.8091 8.5125 14.4285C5.89306 17.048 4.58333 20.2105 4.58333 23.916H0.75ZM12.25 4.74935V0.916016H23.75V4.74935H12.25ZM18 37.3327C14.2944 37.3327 11.1319 36.023 8.5125 33.4035C5.89306 30.7841 4.58333 27.6216 4.58333 23.916C4.58333 20.2105 5.89306 17.048 8.5125 14.4285C11.1319 11.8091 14.2944 10.4993 18 10.4993C21.7056 10.4993 24.8681 11.8091 27.4875 14.4285C30.1069 17.048 31.4167 20.2105 31.4167 23.916C31.4167 27.6216 30.1069 30.7841 27.4875 33.4035C24.8681 36.023 21.7056 37.3327 18 37.3327Z" fill="url(#paint0_linear_1253_2848)" />
                          <defs>
                            <linearGradient id="paint0_linear_1253_2848" x1="18" y1="0.916016" x2="18" y2="41.166" gradientUnits="userSpaceOnUse">
                              <stop stopColor="#63363A" />
                              <stop offset="1" stopColor="#F3949C" />
                            </linearGradient>
                          </defs>
                        </svg>
                        <h4>Save Time</h4>
                        <p>No more manual entry of debts & investments.</p>
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="subscribe-adv-card mb-4">
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M0.666626 31.3337V27.5003H5.93746L5.17079 26.8295C3.60551 25.2642 2.46382 23.5635 1.74571 21.7273C1.0276 19.8912 0.667904 18.0141 0.666626 16.0962C0.666626 12.5503 1.7291 9.39613 3.85404 6.63358C5.97899 3.87102 8.74985 2.04188 12.1666 1.14616V5.17116C9.86663 6.00171 8.01385 7.41557 6.60829 9.41274C5.20274 11.4099 4.49996 13.6377 4.49996 16.0962C4.49996 17.5337 4.77149 18.9315 5.31454 20.2898C5.8576 21.6481 6.70413 22.9016 7.85413 24.0503L8.33329 24.5295V19.8337H12.1666V31.3337H0.666626ZM19.8333 30.8545V26.8295C22.1333 25.9989 23.9861 24.5857 25.3916 22.5898C26.7972 20.5939 27.5 18.3655 27.5 15.9045C27.5 14.467 27.2284 13.0697 26.6854 11.7127C26.1423 10.3557 25.2958 9.1016 24.1458 7.95033L23.6666 7.47116V12.167H19.8333V0.666992H31.3333V4.50033H26.0625L26.8291 5.17116C28.3944 6.73644 29.5367 8.4378 30.2561 10.2752C30.9755 12.1127 31.3346 13.9891 31.3333 15.9045C31.3333 19.4503 30.2715 22.6052 28.1478 25.369C26.0241 28.1328 23.2526 29.9613 19.8333 30.8545Z" fill="url(#paint0_linear_1253_2869)" />
                          <defs>
                            <linearGradient id="paint0_linear_1253_2869" x1="16" y1="0.666992" x2="16" y2="31.3337" gradientUnits="userSpaceOnUse">
                              <stop stopColor="#63363A" />
                              <stop offset="1" stopColor="#C96E76" />
                            </linearGradient>
                          </defs>
                        </svg>
                        <h4>Real-Time Sync</h4>
                        <p>Your balances and net worth always yp to date.</p>
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="subscribe-adv-card">
                        <svg width="42" height="41" viewBox="0 0 42 41" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12.3333 25.7503H10.4167C8.38334 25.7503 6.43329 26.5581 4.99551 27.9959C3.55774 29.4336 2.75 31.3837 2.75 33.417V37.2503M27.7644 21.181C27.891 20.7926 28.1385 20.4548 28.4706 20.2169C28.8028 19.979 29.2023 19.8534 29.6108 19.8585C30.0193 19.8637 30.4155 19.9992 30.7416 20.2454C31.0676 20.4916 31.3065 20.8355 31.4233 21.227L32.8359 24.01C32.9734 24.2805 33.1731 24.5146 33.4187 24.6928C33.6643 24.8711 33.9487 24.9885 34.2485 25.0354L37.3803 25.5261C37.7836 25.5277 38.1761 25.6564 38.502 25.894C38.8279 26.1316 39.0705 26.4659 39.1954 26.8494C39.3202 27.2328 39.3209 27.6459 39.1974 28.0298C39.0739 28.4137 38.8324 28.7489 38.5073 28.9876L36.261 31.2263C36.0459 31.4404 35.8848 31.7025 35.7908 31.9911C35.6969 32.2796 35.6727 32.5864 35.7205 32.8861L36.2169 35.9777C36.3535 36.3652 36.3619 36.7863 36.241 37.1789C36.1201 37.5716 35.8762 37.915 35.5452 38.1585C35.2143 38.402 34.8139 38.5327 34.4031 38.5314C33.9922 38.5301 33.5927 38.3968 33.2633 38.1512L30.4554 36.7137C30.1849 36.5751 29.8853 36.5028 29.5814 36.5028C29.2775 36.5028 28.9779 36.5751 28.7074 36.7137L25.8995 38.1512C25.5702 38.3948 25.1715 38.5264 24.7619 38.5269C24.3522 38.5274 23.9532 38.3966 23.6234 38.1537C23.2935 37.9109 23.0501 37.5688 22.9289 37.1775C22.8077 36.7862 22.815 36.3664 22.9497 35.9796L23.4442 32.888C23.492 32.5883 23.4679 32.2815 23.3739 31.993C23.28 31.7044 23.1188 31.4423 22.9037 31.2282L20.6881 29.0183C20.352 28.7854 20.0992 28.4512 19.9666 28.0645C19.8339 27.6777 19.8285 27.2587 19.9509 26.8687C20.0734 26.4786 20.3174 26.1379 20.6472 25.8963C20.9771 25.6548 21.3756 25.5251 21.7844 25.5261L24.9143 25.0354C25.2141 24.9885 25.4985 24.8711 25.7441 24.6928C25.9897 24.5146 26.1894 24.2805 26.3269 24.01L27.7644 21.181Z" stroke="url(#paint0_linear_1_154)" strokeWidth="3.83333" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M16.1667 18.0833C20.4008 18.0833 23.8333 14.6508 23.8333 10.4167C23.8333 6.18248 20.4008 2.75 16.1667 2.75C11.9325 2.75 8.5 6.18248 8.5 10.4167C8.5 14.6508 11.9325 18.0833 16.1667 18.0833Z" stroke="url(#paint1_linear_1_154)" strokeWidth="3.83333" strokeLinecap="round" strokeLinejoin="round" />
                          <defs>
                            <linearGradient id="paint0_linear_1_154" x1="21.0198" y1="19.8584" x2="21.0198" y2="38.5314" gradientUnits="userSpaceOnUse">
                              <stop stopColor="#63363A" />
                              <stop offset="1" stopColor="#C96E76" />
                            </linearGradient>
                            <linearGradient id="paint1_linear_1_154" x1="16.1667" y1="2.75" x2="16.1667" y2="18.0833" gradientUnits="userSpaceOnUse">
                              <stop stopColor="#63363A" />
                              <stop offset="1" stopColor="#C96E76" />
                            </linearGradient>
                          </defs>
                        </svg>
                        <h4>Advisor Access</h4>
                        <p>Book meetings with financial experts anytime.</p>
                      </div>
                    </div>

                    <div className="col-6">
                      <div className="subscribe-adv-card">
                        <svg width="46" height="46" viewBox="0 0 46 46" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" clipRule="evenodd" d="M36.7211 2.13598C36.0771 -0.722587 32.0291 -0.709444 31.4114 2.15241L31.3457 2.47113L32.9491 2.81613L31.3457 2.47441C30.9928 4.17027 30.1422 5.72235 28.9025 6.93217C27.6628 8.14199 26.0905 8.95456 24.3865 9.26598C21.4425 9.78841 21.4425 14.0401 24.3865 14.5626C26.0896 14.8735 27.6613 15.6851 28.9009 16.8937C30.1405 18.1023 30.9917 19.6529 31.3457 21.3476L31.4114 21.6663C32.0291 24.5248 36.0771 24.5478 36.7211 21.6893L36.8033 21.318C37.1722 19.6294 38.0317 18.0874 39.2738 16.8855C40.516 15.6837 42.0856 14.8755 43.7854 14.5626C46.736 14.0401 46.736 9.78184 43.7854 9.25941C42.0862 8.94585 40.5173 8.13745 39.2758 6.93564C38.0342 5.73383 37.1752 4.19214 36.8065 2.50398L36.7211 2.13598ZM18.0517 10.9976C14.904 8.9637 11.8154 7.51141 9.10141 6.90027C6.48269 6.30884 3.64055 6.39098 1.72826 8.30656C0.305548 9.72598 -0.0985949 11.6777 0.0164052 13.5933C0.131405 15.5121 0.772119 17.6446 1.76769 19.8328C2.69755 21.8798 3.97898 24.0648 5.55612 26.2893C3.97898 28.5137 2.69755 30.6987 1.76769 32.7457C0.772119 34.934 0.131405 37.0664 0.0164052 38.9886C-0.0985949 40.9008 0.305548 42.8526 1.72498 44.272L3.18055 42.8197L1.72826 44.272C3.14769 45.6947 5.09941 46.0988 7.01498 45.9838C8.93383 45.8688 11.0663 45.2281 13.2545 44.2326C15.5232 43.1765 17.6862 41.9069 19.7143 40.4408C21.9387 42.018 24.1237 43.2994 26.1707 44.2293C28.359 45.2248 30.4914 45.8656 32.4103 45.9806C34.3258 46.0956 36.2775 45.6914 37.7003 44.272C39.6881 42.2776 39.7013 39.2908 39.0211 36.5571C38.3147 33.7117 36.7113 30.4621 34.4803 27.1666C34.1753 26.7152 33.7034 26.4034 33.1686 26.2999C32.6337 26.1964 32.0797 26.3096 31.6283 26.6146C31.1769 26.9196 30.8651 27.3914 30.7616 27.9262C30.6581 28.4611 30.7713 29.0152 31.0763 29.4666C33.1528 32.5321 34.4836 35.3316 35.0355 37.5494C35.6138 39.8724 35.2064 40.9501 34.7924 41.3641C34.5033 41.6598 33.8987 41.9556 32.6567 41.8833C31.418 41.8077 29.7981 41.3641 27.8694 40.4901C26.3908 39.8166 24.7907 38.913 23.1314 37.809C25.3728 35.9446 27.4808 33.9255 29.44 31.7666C29.6216 31.567 29.7622 31.3336 29.8537 31.0797C29.9451 30.8258 29.9857 30.5564 29.973 30.2869C29.9604 30.0173 29.8947 29.7529 29.7799 29.5087C29.665 29.2645 29.5032 29.0453 29.3036 28.8636C29.1041 28.682 28.8707 28.5414 28.6168 28.4499C28.3629 28.3585 28.0935 28.3179 27.8239 28.3306C27.5544 28.3433 27.2899 28.4089 27.0457 28.5237C26.8015 28.6386 26.5823 28.8004 26.4007 29C24.3348 31.2661 22.0996 33.3717 19.7143 35.2987C18.1371 34.0337 16.5403 32.6044 14.9664 31.0338C13.4604 29.5291 12.0367 27.9442 10.7015 26.286C12.7807 23.7028 15.0735 21.299 17.5555 19.1001C17.9629 18.7385 18.21 18.2298 18.2423 17.686C18.2747 17.1422 18.0897 16.6079 17.7281 16.2005C17.3664 15.7931 16.8577 15.5461 16.314 15.5137C15.7702 15.4813 15.2358 15.6663 14.8284 16.028C12.4501 18.1412 10.2316 20.4277 8.19126 22.8688C7.17297 21.3633 6.27641 19.779 5.51012 18.1308C4.63612 16.2054 4.19255 14.5823 4.11698 13.3436C4.04469 12.1016 4.34369 11.497 4.63283 11.2078C5.02712 10.8136 6.03583 10.416 8.19783 10.9023C10.2711 11.3721 12.903 12.5616 15.8174 14.4508C16.2749 14.7471 16.8314 14.8495 17.3644 14.7355C17.8974 14.6215 18.3633 14.3005 18.6595 13.843C18.9558 13.3855 19.0582 12.829 18.9442 12.296C18.8303 11.763 18.5092 11.2971 18.0517 11.0008M8.19126 29.7031C7.08398 31.3624 6.18369 32.9626 5.51012 34.4411C4.63612 36.3666 4.19255 37.9897 4.11698 39.2284C4.04469 40.4704 4.34369 41.075 4.63283 41.3641C4.92855 41.6598 5.52983 41.9556 6.76855 41.8833C8.01055 41.8077 9.63369 41.3641 11.5591 40.4901C13.0377 39.8166 14.6378 38.9163 16.2971 37.809C13.356 35.3573 10.643 32.6443 8.19126 29.7031ZM15.6071 26.286C15.6071 25.1967 16.0398 24.152 16.8101 23.3818C17.5803 22.6116 18.625 22.1788 19.7143 22.1788C20.8035 22.1788 21.8482 22.6116 22.6185 23.3818C23.3887 24.152 23.8214 25.1967 23.8214 26.286C23.8214 27.3753 23.3887 28.4199 22.6185 29.1902C21.8482 29.9604 20.8035 30.3931 19.7143 30.3931C18.625 30.3931 17.5803 29.9604 16.8101 29.1902C16.0398 28.4199 15.6071 27.3753 15.6071 26.286Z" fill="url(#paint0_linear_1253_2875)" />
                          <defs>
                            <linearGradient id="paint0_linear_1253_2875" x1="22.9981" y1="-0.000976563" x2="22.9981" y2="46.0023" gradientUnits="userSpaceOnUse">
                              <stop stopColor="#63363A" />
                              <stop offset="1" stopColor="#C96E76" />
                            </linearGradient>
                          </defs>
                        </svg>
                        <h4>Smarter Insights</h4>
                        <p>Personalized planning based on verified data.</p>
                      </div>
                    </div>

                    <div className="col-12 text-center">
                      <Link
                        to="/package"
                        className="as-btn-primary mt-3"
                      >
                        <svg className="mr-2" width="18" height="19" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" clipRule="evenodd" d="M2.14114 12.5019C2.14072 10.4177 2.80085 8.38693 4.02672 6.70132C5.25259 5.01571 6.98113 3.76196 8.96409 3.12015C10.9471 2.47833 13.0824 2.48147 15.0635 3.12911C17.0445 3.77675 18.7694 5.03557 19.9903 6.72478L19.1074 7.60764C18.9872 7.72751 18.9054 7.88038 18.8722 8.04685C18.839 8.21332 18.8559 8.3859 18.9209 8.54272C18.9859 8.69953 19.096 8.83351 19.2373 8.92767C19.3785 9.02183 19.5445 9.07194 19.7143 9.07164H23.1429C23.3702 9.07164 23.5882 8.98133 23.7489 8.82058C23.9097 8.65984 24 8.44182 24 8.21449V4.78592C24.0003 4.61617 23.9502 4.45015 23.856 4.30892C23.7619 4.16768 23.6279 4.05758 23.4711 3.99258C23.3143 3.92758 23.1417 3.9106 22.9752 3.9438C22.8087 3.977 22.6559 4.05888 22.536 4.17906L21.5211 5.19564C19.9944 3.20716 17.8833 1.7467 15.4841 1.01927C13.085 0.291836 10.5183 0.333958 8.1443 1.13972C5.77032 1.94548 3.70826 3.47443 2.24758 5.51193C0.786896 7.54944 0.00092691 9.99321 0 12.5002C0 12.7844 0.112882 13.0569 0.313814 13.2578C0.514746 13.4588 0.787268 13.5716 1.07143 13.5716C1.35559 13.5716 1.62811 13.4588 1.82904 13.2578C2.02997 13.0569 2.14286 12.7844 2.14286 12.5002M12 5.42878C12.5914 5.42878 13.0714 5.90878 13.0714 6.50021V7.45506C13.6328 7.52955 14.1627 7.75794 14.6023 8.11497C15.042 8.472 15.3742 8.94372 15.5623 9.47792C15.6147 9.61176 15.6396 9.75478 15.6356 9.89845C15.6315 10.0421 15.5986 10.1835 15.5388 10.3142C15.479 10.4449 15.3935 10.5622 15.2874 10.6592C15.1814 10.7562 15.0568 10.8308 14.9213 10.8787C14.7858 10.9265 14.642 10.9466 14.4986 10.9378C14.3551 10.9289 14.2149 10.8913 14.0863 10.8272C13.9577 10.763 13.8433 10.6736 13.7499 10.5643C13.6566 10.4551 13.5861 10.3281 13.5429 10.1911C13.4792 10.0097 13.3606 9.85274 13.2036 9.74184C13.0466 9.63095 12.859 9.57166 12.6669 9.57221H11.1189C10.942 9.57049 10.7707 9.63459 10.6385 9.75205C10.5062 9.8695 10.4223 10.0319 10.403 10.2078C10.3838 10.3836 10.4306 10.5604 10.5344 10.7036C10.6382 10.8469 10.7915 10.9465 10.9646 10.9831L13.3234 11.4973C14.0305 11.6508 14.6607 12.0489 15.103 12.6215C15.5453 13.1941 15.7713 13.9044 15.7412 14.6273C15.711 15.3502 15.4267 16.0393 14.9383 16.5731C14.4499 17.1069 13.7888 17.4512 13.0714 17.5454V18.5002C13.0714 18.7844 12.9585 19.0569 12.7576 19.2578C12.5567 19.4588 12.2842 19.5716 12 19.5716C11.7158 19.5716 11.4433 19.4588 11.2424 19.2578C11.0415 19.0569 10.9286 18.7844 10.9286 18.5002V17.5454C10.3672 17.4709 9.83729 17.2425 9.39767 16.8854C8.95804 16.5284 8.62579 16.0567 8.43771 15.5225C8.38533 15.3886 8.36042 15.2456 8.36445 15.102C8.36848 14.9583 8.40138 14.8169 8.46118 14.6862C8.52099 14.5555 8.60647 14.4382 8.71256 14.3412C8.81864 14.2442 8.94316 14.1696 9.07868 14.1217C9.21421 14.0739 9.35798 14.0538 9.50144 14.0626C9.64489 14.0715 9.7851 14.1091 9.91372 14.1733C10.0423 14.2374 10.1567 14.3268 10.2501 14.4361C10.3434 14.5453 10.4139 14.6723 10.4571 14.8094C10.5209 14.9907 10.6394 15.1477 10.7964 15.2586C10.9534 15.3695 11.141 15.4288 11.3331 15.4282H12.6669C12.8954 15.4274 13.1156 15.3424 13.2853 15.1894C13.4551 15.0365 13.5626 14.8263 13.5872 14.5991C13.6118 14.3719 13.5518 14.1436 13.4187 13.9579C13.2856 13.7721 13.0888 13.6419 12.8657 13.5922L10.5086 13.0762C9.8363 12.9294 9.23962 12.5449 8.82827 11.9933C8.41691 11.4416 8.21855 10.76 8.26965 10.0738C8.32075 9.38763 8.61788 8.74294 9.1064 8.25834C9.59492 7.77373 10.242 7.48179 10.9286 7.43621V6.50021C10.9286 5.90878 11.4086 5.42878 12 5.42878ZM0.529714 21.0065C0.372966 20.9417 0.238964 20.8319 0.144669 20.6909C0.050375 20.5499 2.75328e-05 20.3841 0 20.2145L0 16.7859C0 16.5586 0.0903059 16.3406 0.251051 16.1798C0.411797 16.0191 0.629814 15.9288 0.857143 15.9288H4.28571C4.45546 15.9285 4.62148 15.9786 4.76272 16.0727C4.90396 16.1669 5.01406 16.3009 5.07906 16.4577C5.14406 16.6145 5.16104 16.7871 5.12784 16.9536C5.09464 17.12 5.01275 17.2729 4.89257 17.3928L4.00971 18.2739C5.23063 19.9631 6.95547 21.2219 8.93654 21.8696C10.9176 22.5172 13.0529 22.5204 15.0359 21.8786C17.0189 21.2367 18.7474 19.983 19.9733 18.2974C21.1991 16.6118 21.8593 14.581 21.8589 12.4968C21.859 12.3561 21.8868 12.2168 21.9407 12.0868C21.9947 11.9569 22.0737 11.8388 22.1733 11.7394C22.2728 11.64 22.391 11.5612 22.5211 11.5074C22.6511 11.4537 22.7904 11.4261 22.9311 11.4262C23.0718 11.4263 23.2111 11.4541 23.3411 11.5081C23.471 11.562 23.5891 11.6411 23.6885 11.7406C23.7879 11.8402 23.8667 11.9584 23.9205 12.0884C23.9742 12.2184 24.0018 12.3578 24.0017 12.4985C24.0016 15.0061 23.2161 17.4506 21.7554 19.4889C20.2948 21.5271 18.2324 23.0567 15.8579 23.8627C13.4835 24.6688 10.9161 24.7109 8.51651 23.9831C6.11688 23.2552 4.0055 21.7941 2.47886 19.8048L1.464 20.8214C1.34404 20.9411 1.19124 21.0227 1.02494 21.0556C0.858637 21.0886 0.686297 21.0715 0.529714 21.0065Z" fill="white" />
                        </svg>

                        Get Subscription
                      </Link>
                    </div>
                  </div>
                </div>
              )}
              {JSON.parse(localStorage.getItem("currentUser"))?.user?.userType === 'advisor' && <AdvisorReviewsCard />}
              {JSON.parse(localStorage.getItem("currentUser"))?.user?.userType === 'client' && !isPaymentIncomplete && <Subscription handleCancelStatus={refressUser} />}
            </div>
          </div>
        </div>
      ) :
        (<Loader />)}


      <ChangePasswordModal
        show={changePasswordPopupShow}
        onClose={changePasswordPopupClose}
        enqueueSnackbar={enqueueSnackbar}
        showOldPassword={showOldPassword}
        setShowOldPassword={setShowOldPassword}
        showNewPassword={showNewPassword}
        setShowNewPassword={setShowNewPassword}
        showConfirmPassword={showConfirmPassword}
        setShowConfirmPassword={setShowConfirmPassword}
      />
    </>
  );
};

export default Profile;
